import { Component, lazy, Suspense } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Layout from "./components/layout/Layout";
import LoadingSpinner from "./components/common/LoadingSpinner";
import AuthGate from "./components/auth/AuthGate";
import { webLogger } from "./logging/logger";

const HomePage = lazy(() => import("./pages/HomePage"));
const ContractsPage = lazy(() => import("./pages/ContractsPage"));
const PerformanceFiguresPage = lazy(
  () => import("./pages/PerformanceFiguresPage"),
);
const LegalEntityMappingsPage = lazy(
  () => import("./pages/LegalEntityMappingsPage"),
);
const CostManagementPage = lazy(() => import("./pages/CostManagementPage"));
const SignedOutPage = lazy(() => import("./pages/SignedOutPage"));
const AuthGatePreview = lazy(
  () => import("./components/auth/AuthGatePreview"),
);

interface ErrorBoundaryState {
  error: Error | null;
}

class ErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const componentStack = (errorInfo.componentStack ?? "")
      .trim()
      .replace(/\s+/g, " ")
      .slice(0, 300);

    webLogger.error({
      handler: "ErrorBoundary",
      path: window.location.pathname,
      message: `React render failed on page ${window.location.pathname}. Error: ${error.message}.${componentStack ? ` Component stack: ${componentStack}` : ""}`,
    });
  }
  render() {
    if (this.state.error) {
      return (
        <div
          style={{ padding: "var(--wb-spacing-m)", color: "var(--wb-red-45)" }}
        >
          <strong>Page error:</strong> {this.state.error.message}
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <Router>
      <ErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/signed-out" element={<SignedOutPage />} />
            <Route path="/auth-preview" element={<AuthGatePreview />} />
            <Route
              path="*"
              element={
                <AuthGate>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/contracts" element={<ContractsPage />} />
                      <Route
                        path="/performance"
                        element={<PerformanceFiguresPage />}
                      />
                      <Route
                        path="/legal-entity-mappings"
                        element={<LegalEntityMappingsPage />}
                      />
                      <Route
                        path="/cost-management"
                        element={<CostManagementPage />}
                      />
                    </Routes>
                  </Layout>
                </AuthGate>
              }
            />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
